/**
 * Publications Duplication Renderer
 *
 * Displays duplication batch process details:
 * operation description, target channels with icons, publication status, and item count.
 *
 * Data shape (from PublicationsDuplicationBatchProcessStartedEventSchema):
 *   publicationsIds: string[]
 *   channels: Array<{ connectorId, integrationUserId, ... }>
 *   statusToPublish: 'active' | 'paused'
 */

import { FC } from 'react';
import { Badge, Group, Stack, Text, ThemeIcon } from '@mantine/core';
import { IconPlug } from '@tabler/icons-react';
import type { DetailRendererProps, OperationRendererConfig, ProcessData } from './index';

const DetailComponent: FC<DetailRendererProps> = ({ process, resolved, t }) => {
  const data = ( process.data || {}) as ProcessData;
  const channels = data.channels || [];
  const statusToPublish = data.statusToPublish || 'active';
  const publicationsCount = data.publicationsIds?.length ?? process.totalItems ?? 0;
  const statusLabel = t( `batchProcesses_renderer_${statusToPublish}` );

  return (
    <Stack gap="sm">
      <Text fz="sm" c="dimmed">
        {t( 'batchProcesses_operations_PublicationsDuplication_description' )}
      </Text>

      <Group gap="xs">
        <Text fz="sm" fw={500}>
          {t( 'batchProcesses_renderer_statusToPublish' )}:
        </Text>
        <Badge
          size="sm"
          color={statusToPublish === 'active' ? 'primary' : 'yellow'}
          variant="light"
        >
          {statusLabel}
        </Badge>
      </Group>

      <Group gap="xs">
        <Text fz="sm" fw={500}>
          {t( 'batchProcesses_renderer_publicationCount' )}:
        </Text>
        <Text fz="sm">
          {publicationsCount}
        </Text>
      </Group>

      {channels.length > 0 && (
        <Stack gap="xs">
          <Text fz="sm" fw={500}>
            {t( 'batchProcesses_renderer_targetChannels' )}:
          </Text>
          {channels.map(( ch: { connectorId: string }, idx: number ) => {
            const resolvedChannel = resolved.resolvedChannels[ch.connectorId];
            return (
              <Group key={ch.connectorId || idx} gap="xs">
                <ThemeIcon size="sm" variant="light" color="gray">
                  <IconPlug size={14} />
                </ThemeIcon>
                <Text fz="sm">
                  {resolvedChannel?.name ?? ch.connectorId}
                </Text>
              </Group>
            );
          })}
        </Stack>
      )}
    </Stack>
  );
};

export const config: Omit<OperationRendererConfig, 'icon' | 'color' | 'labelKey'> = {
  getSummary: ( data, t, _process, resolvedChannels ) => {
    const channels = data?.channels || [];
    const statusToPublish = data?.statusToPublish || 'active';
    const statusLabel = t( `batchProcesses_renderer_${statusToPublish}` );

    if ( channels.length > 0 ) {
      const firstChannel = channels[0];
      const resolved = resolvedChannels?.[firstChannel.connectorId];
      const channelName = resolved?.name ?? firstChannel.connectorId;
      return `${channelName} - ${statusLabel}`;
    }

    return statusLabel;
  },
  DetailComponent,
  getRequiredReferenceIds: ( process ) => {
    const data = ( process?.data || {}) as ProcessData;
    return {
      publicationIds: data.publicationsIds || [],
      channelIds: data.channels?.map(( c: { connectorId: string }) => c.connectorId ) || []
    };
  }
};

export default DetailComponent;
